// ============================================
// パスアニメーション（GSAP MotionPath + ScrollTrigger）
// スクロールに連動してblue/yellowの背景円をS字カーブで移動
// ============================================
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

/**
 * 隠しSVGをコンテナ内に作成し、モーションパスを追加
 * viewBoxをコンテナのピクセルサイズに合わせることで、
 * パス座標 = ビューポートピクセル座標となる
 */
function createMotionSvg(container, pathId, pathD, vw, vh) {
	const svgNS = 'http://www.w3.org/2000/svg';
	const svg = document.createElementNS(svgNS, 'svg');
	svg.setAttribute('viewBox', `0 0 ${vw} ${vh}`);
	svg.style.cssText =
		'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;opacity:0;';

	const path = document.createElementNS(svgNS, 'path');
	path.setAttribute('d', pathD);
	path.setAttribute('fill', 'none');
	path.id = pathId;
	svg.appendChild(path);
	container.appendChild(svg);
}

/**
 * 要素のCSS初期位置（コンテナ内の中心座標）を取得
 */
function getElementCenter(el, containerRect) {
	const rect = el.getBoundingClientRect();
	return {
		x: rect.left - containerRect.left + rect.width / 2,
		y: rect.top - containerRect.top + rect.height / 2,
	};
}

/**
 * パスアニメーション初期化（blue + yellow）
 */
export function initMotionPath() {
	const container = document.querySelector('.bg-home-object');
	if (!container) return;

	const blueEl = container.querySelector('.is-blue');
	const yellowEl = container.querySelector('.is-yellow');
	if (!blueEl || !yellowEl) return;

	/* ビューポート（コンテナ）サイズ取得 */
	const vw = container.offsetWidth;
	const vh = container.offsetHeight;
	const containerRect = container.getBoundingClientRect();

	/* CSS初期位置を取得（エレメント中心座標） */
	const blueStart = getElementCenter(blueEl, containerRect);
	const yellowStart = getElementCenter(yellowEl, containerRect);

	/* オブジェクトの高さを取得（終点調整用） */
	const blueH = blueEl.offsetHeight;
	const yellowH = yellowEl.offsetHeight;

	/* 変更: yellowの公転パラメータ
	 * blueを中心にyellowが周回する（地球と月のイメージ）
	 * 初期オフセットから公転半径と開始角度を算出 */
	const offsetX = yellowStart.x - blueStart.x;
	const offsetY = yellowStart.y - blueStart.y;
	const orbitRadius = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
	const startAngle = Math.atan2(offsetY, offsetX);
	const orbits = 1.5; // 変更: 2周→1.5周（終盤の重なりを防止）

	/* blueのパス通過点を定義（S字カーブ） */
	const bp = [
		{ x: blueStart.x, y: blueStart.y },                                     // 始点
		{ x: blueStart.x - vw * 0.1, y: blueStart.y + vh * 0.15 },             // CP1
		{ x: vw * 0.08, y: vh * 0.3 },                                          // CP2
		{ x: vw * 0.2, y: vh * 0.4 },                                           // P1
		{ x: vw * 0.15, y: vh * 0.5 },                                          // CP3
		{ x: vw * 0.3, y: vh * 0.6 },                                           // CP4
		{ x: vw * 0.25, y: vh * 0.65 },                                         // P2
		{ x: vw * 0.2, y: vh * 0.75 },                                          // CP5
		{ x: vw * 0.08, y: vh * 0.85 },                                         // CP6
		{ x: vw * 0.18, y: vh * 0.95 - blueH / vh },                            // P3（終点）
	];

	/* ── blue: 右上 → 左寄りに下降するS字カーブ ── */
	const bluePathD = `
		M ${bp[0].x},${bp[0].y}
		C ${bp[1].x},${bp[1].y}
			${bp[2].x},${bp[2].y}
			${bp[3].x},${bp[3].y}
		C ${bp[4].x},${bp[4].y}
			${bp[5].x},${bp[5].y}
			${bp[6].x},${bp[6].y}
		C ${bp[7].x},${bp[7].y}
			${bp[8].x},${bp[8].y}
			${bp[9].x},${bp[9].y}
	`;

	/* ── yellow: blueの周囲を公転するパスを生成 ──
	 * 各制御点でblueの位置を中心に、角度を回転させてオフセット
	 * 変更: 終盤ほど公転半径を広げて重なりを防止（1.0→1.3倍） */
	const totalPoints = bp.length;
	const yp = bp.map((point, i) => {
		const progress = i / (totalPoints - 1);
		const angle = startAngle + progress * Math.PI * 2 * orbits;
		const radiusScale = 1 + progress * 0.3;
		return {
			x: point.x + Math.cos(angle) * orbitRadius * radiusScale,
			y: point.y + Math.sin(angle) * orbitRadius * radiusScale,
		};
	});

	const yellowPathD = `
		M ${yp[0].x},${yp[0].y}
		C ${yp[1].x},${yp[1].y}
			${yp[2].x},${yp[2].y}
			${yp[3].x},${yp[3].y}
		C ${yp[4].x},${yp[4].y}
			${yp[5].x},${yp[5].y}
			${yp[6].x},${yp[6].y}
		C ${yp[7].x},${yp[7].y}
			${yp[8].x},${yp[8].y}
			${yp[9].x},${yp[9].y}
	`;

	/* 隠しSVGをコンテナに追加 */
	createMotionSvg(container, 'motionpath-blue', bluePathD, vw, vh);
	createMotionSvg(container, 'motionpath-yellow', yellowPathD, vw, vh);

	/* CSS位置をリセット（GSAPのalignが位置を制御するため） */
	gsap.set(blueEl, { top: 0, left: 0, right: 'auto' });
	gsap.set(yellowEl, { top: 0, left: 0, right: 'auto' });

	/* ── blue: ScrollTrigger連動アニメーション ── */
	gsap.to(blueEl, {
		motionPath: {
			path: '#motionpath-blue',
			align: '#motionpath-blue',
			alignOrigin: [0.5, 0.5],
			autoRotate: false,
		},
		scrollTrigger: {
			trigger: document.documentElement,
			start: 'top top',
			end: 'bottom bottom',
			scrub: 1,
		},
		ease: 'none',
	});

	/* ── yellow: ScrollTrigger連動アニメーション ── */
	gsap.to(yellowEl, {
		motionPath: {
			path: '#motionpath-yellow',
			align: '#motionpath-yellow',
			alignOrigin: [0.5, 0.5],
			autoRotate: false,
		},
		scrollTrigger: {
			trigger: document.documentElement,
			start: 'top top',
			end: 'bottom bottom',
			scrub: 1,
		},
		ease: 'none',
	});
}
